import { FAIcon } from '@/components'
import { cn } from '@/lib/utils'
import type { FontAwesomeIcon } from '@ur-solutions/react-components'
import React from 'react'

/**
 * A titled card grouping a set of issue detail fields. The header is dark and
 * larger than the field labels so card titles read as titles, matching the
 * Absence page's card layout.
 */
export const DetailsSection: React.FC<
  React.PropsWithChildren<{ title: string; icon?: FontAwesomeIcon }>
> = ({ title, icon, children }) => (
  <section className="rounded-2xl bg-white shadow-card">
    <header className="flex items-center gap-2.5 border-b border-stone-200 px-5 py-4 sm:px-6">
      {icon && (
        <FAIcon
          className="text-stone-500"
          icon={icon}
          type="regular"
          fixedWidth
        />
      )}
      <h3 className="text-base font-semibold text-stone-900">{title}</h3>
    </header>
    <div className="grid gap-x-6 gap-y-4 px-5 py-5 sm:grid-cols-2 sm:px-6">
      {children}
    </div>
  </section>
)

/**
 * A single labelled field within a DetailsSection. `full` makes it span both
 * columns (use for text areas); short fields pair up two-per-row.
 */
export const DetailsField: React.FC<
  React.PropsWithChildren<{
    label: React.ReactNode
    required?: boolean
    full?: boolean
    /** Raise the field's stacking context so an open dropdown (e.g. the
     *  location autocomplete) paints above the fields below it. */
    elevated?: boolean
  }>
> = ({ label, required, full, elevated, children }) => (
  <div
    className={cn(
      'flex min-w-0 flex-col',
      full && 'sm:col-span-2',
      elevated && 'relative z-20'
    )}
  >
    <h4 className="mb-1.5 flex items-center gap-1 text-xs font-semibold uppercase text-stone-500">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </h4>
    <div className="text-[0.95rem] font-medium text-stone-900">{children}</div>
  </div>
)
